import type { KeyboardEvent } from 'react';
import { memo, useCallback } from 'react';
import { Box, Text } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';

import { ICON_SIZE_SM } from '@/constants/ui';
import type { WorkspaceMenuItemProps } from './WorkspaceSelector.types';
import { WorkspaceAvatar } from './WorkspaceAvatar';
import styles from './WorkspaceSelector.module.css';

export const WorkspaceMenuItem = memo(function WorkspaceMenuItem({
  workspace,
  isActive,
  onSelect,
}: WorkspaceMenuItemProps) {
  const handleSelect = useCallback(() => {
    onSelect(workspace);
  }, [onSelect, workspace]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleSelect();
      }
    },
    [handleSelect]
  );

  return (
    <Box
      className={`${styles.item} ${isActive ? styles.activeItem : ''}`}
      onClick={handleSelect}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <WorkspaceAvatar name={workspace.name} size="md" shape="rounded" />
      <div className={styles.itemContent}>
        <Text
          size="sm"
          fw={isActive ? 600 : 400}
          lineClamp={1}
          className={isActive ? styles.activeItemName : undefined}
        >
          {workspace.name}
        </Text>
        {workspace.description && (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {workspace.description}
          </Text>
        )}
      </div>
      {isActive && (
        <IconCheck size={ICON_SIZE_SM} className={styles.itemCheck} />
      )}
    </Box>
  );
});
